import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import api from "../../services/api";
import "./SectorMaster.css";
import {usePermission} from "../../hooks/usePermissions";
import { formatAdminDate } from "../../utils/datetime";


const SearchIcon = () => (
  <svg
    width="15"
    height="15"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="11" cy="11" r="8" />
    <line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
);

const TrashIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
    <path d="M10 11v6" />
    <path d="M14 11v6" />
    <path d="M9 6V4h6v2" />
  </svg>
);

const EditIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M12 20h9" />
    <path d="M16.5 3.5a2.12 2.12 0 1 1 3 3L7 19l-4 1 1-4Z" />
  </svg>
);

const CloseIcon = () => (
  <svg
    width="15"
    height="15"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
  >
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

const DeleteModal = ({ item, onClose, onConfirm, submitting }) => (
  <div
    className="sm-modal-overlay"
    onClick={(e) => e.target === e.currentTarget && !submitting && onClose()}
  >
    <div className="sm-modal" style={{ maxWidth: 400 }}>
      <div className="sm-modal-header">
        <h2>Delete Sector</h2>
        <button
          type="button"
          className="sm-modal-close"
          onClick={onClose}
          disabled={submitting}
        >
          <CloseIcon />
        </button>
      </div>
      <div
        className="sm-modal-body"
        style={{ textAlign: "center", padding: "28px 24px" }}
      >
        <div className="sm-confirm-icon">Delete</div>
        <p className="sm-confirm-msg">Are you sure you want to delete</p>
        <p className="sm-confirm-name">"{item.sector_name}"?</p>
        <p
          className="sm-confirm-msg"
          style={{ marginTop: 8, fontSize: 12, color: "#dc2626" }}
        >
          This action cannot be undone.
        </p>
      </div>
      <div className="sm-modal-footer" style={{ justifyContent: "center" }}>
        <button
          type="button"
          className="sm-btn-cancel"
          onClick={onClose}
          disabled={submitting}
        >
          Cancel
        </button>
        <button
          type="button"
          style={{
            padding: "9px 26px",
            borderRadius: 9,
            border: "none",
            background: "#dc2626",
            color: "#fff",
            fontWeight: 600,
            fontSize: 14,
            fontFamily: "inherit",
            cursor: "pointer",
            opacity: submitting ? 0.7 : 1,
          }}
          onClick={() => onConfirm(item.id)}
          disabled={submitting}
        >
          {submitting ? "Deleting..." : "Yes, Delete"}
        </button>
      </div>
    </div>
  </div>
);

const AddModal = ({ initialData, mode, onClose, onSave, submitting }) => {
  const [form, setForm] = useState({
    sector_code: initialData?.sector_code ?? "",
    sector_name: initialData?.sector_name ?? "",
    brief_description: initialData?.brief_description ?? "",
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave({
      sector_code: Number(form.sector_code),
      sector_name: form.sector_name.trim(),
      brief_description: form.brief_description.trim(),
    });
  };

  return (
    <div
      className="sm-modal-overlay"
      onClick={(e) => e.target === e.currentTarget && !submitting && onClose()}
    >
      <div className="sm-modal">
        <div className="sm-modal-header">
          <h2>{mode === "edit" ? "Edit Sector" : "Add New Sector"}</h2>
          <button
            type="button"
            className="sm-modal-close"
            onClick={onClose}
            disabled={submitting}
          >
            <CloseIcon />
          </button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="sm-modal-body">
            <div className="sm-form-row">
              <div className="sm-form-group">
                <label>
                  Sector Code <span className="required">*</span>
                </label>
                <input
                  className="sm-input"
                  type="number"
                  required
                  value={form.sector_code}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, sector_code: e.target.value }))
                  }
                  placeholder="e.g. 101"
                />
              </div>
              <div className="sm-form-group">
                <label>
                  Sector Name <span className="required">*</span>
                </label>
                <input
                  className="sm-input"
                  type="text"
                  required
                  value={form.sector_name}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, sector_name: e.target.value }))
                  }
                  placeholder="e.g. IT"
                />
              </div>
            </div>
            <div className="sm-form-row">
              <div className="sm-form-group full">
                <label>Brief Description</label>
                <textarea
                  className="sm-textarea"
                  value={form.brief_description}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      brief_description: e.target.value,
                    }))
                  }
                  placeholder="Enter description..."
                />
              </div>
            </div>
          </div>
          <div className="sm-modal-footer">
            <button
              type="button"
              className="sm-btn-cancel"
              onClick={onClose}
              disabled={submitting}
            >
              Cancel
            </button>
            <button type="submit" className="sm-btn-save" disabled={submitting}>
              {submitting
                ? mode === "edit"
                  ? "Updating..."
                  : "Saving..."
                : mode === "edit"
                  ? "Update Sector"
                  : "Save Sector"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const SectorMaster = () => {
  const [sectors, setSectors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [descriptionFilter, setDescriptionFilter] = useState("all");
  const [sortKey, setSortKey] = useState("sector_code");
  const [sortDir, setSortDir] = useState("asc");
  const [currentPage, setCurrentPage] = useState(1);
  const [modalMode, setModalMode] = useState(null);
  const [selectedSector, setSelectedSector] = useState(null);
  const {hasPermission} = usePermission();

  const itemsPerPage = 8;

  const fetchSectors = async () => {
    try {
      setLoading(true);
      const response = await api.get("/sectormaster", {
        params: {
          search: appliedSearch || undefined,
          description_filter:
            descriptionFilter !== "all" ? descriptionFilter : undefined,
          sort_by: sortKey,
          sort_dir: sortDir,
        },
      });
      setSectors(Array.isArray(response?.data) ? response.data : []);
    } catch (error) {
      toast.error(error?.message || "Failed to load sectors");
      setSectors([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSectors();
  }, [appliedSearch, descriptionFilter, sortKey, sortDir]);

  useEffect(() => {
    setCurrentPage(1);
  }, [appliedSearch, descriptionFilter, sortKey, sortDir]);

  const filtered = useMemo(() => sectors, [sectors]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / itemsPerPage));
  const paginatedSectors = filtered.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDir((prev) => (prev === "asc" ? "desc" : "asc"));
      return;
    }

    setSortKey(key);
    setSortDir(key === "sector_name" || key === "brief_description" ? "asc" : "desc");
  };

  const sortIcon = (key) =>
    sortKey === key ? (sortDir === "asc" ? "▲" : "▼") : "⇅";

  const handleSearch = () => {
    setAppliedSearch(searchQuery);
  };

  const handleSearchKey = (e) => {
    if (e.key === "Enter") handleSearch();
  };

  const handleDeletePrompt = (sector) => {
    setSelectedSector(sector);
    setModalMode("delete");
  };

  const handleEditPrompt = (sector) => {
    setSelectedSector(sector);
    setModalMode("edit");
  };

  const handleSaveAdd = async (payload) => {
    if (!payload.sector_code || !payload.sector_name) {
      toast.error("Sector code and sector name are required");
      return;
    }

    try {
      setSaving(true);
      const response = await api.post("/sectormaster", payload);
      setSectors((prev) => [response.data, ...prev]);
      setModalMode(null);
      toast.success("Sector added successfully");
    } catch (error) {
      toast.error(error?.message || "Failed to add sector");
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async (payload) => {
    if (!selectedSector?.id) return;
    if (!payload.sector_code || !payload.sector_name) {
      toast.error("Sector code and sector name are required");
      return;
    }

    try {
      setSaving(true);
      const response = await api.put(`/sectormaster/${selectedSector.id}`, payload);
      setSectors((prev) =>
        prev.map((sector) => (sector.id === selectedSector.id ? response.data : sector))
      );
      setModalMode(null);
      setSelectedSector(null);
      toast.success("Sector updated successfully");
    } catch (error) {
      toast.error(error?.message || "Failed to update sector");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteConfirm = async (id) => {
    try {
      setDeleting(true);
      await api.delete(`/sectormaster/${id}`);
      setSectors((prev) => prev.filter((sector) => sector.id !== id));
      setModalMode(null);
      setSelectedSector(null);
      toast.success("Sector deleted successfully");
    } catch (error) {
      toast.error(error?.message || "Failed to delete sector");
    } finally {
      setDeleting(false);
    }
  };

  const SkeletonRows = () => (
    <>
      {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
        <tr key={i} className="sm-skeleton-row">
          <td>
            <span className="sm-skel" style={{ width: 40 }} />
          </td>
          <td>
            <span className="sm-skel" style={{ width: 80 }} />
          </td>
          <td>
            <span className="sm-skel" style={{ width: 140 }} />
          </td>
          <td>
            <span className="sm-skel" style={{ width: 200 }} />
          </td>
          <td>
            <span className="sm-skel" style={{ width: 52 }} />
          </td>
        </tr>
      ))}
    </>
  );

  const ThCol = ({ col, label, center }) => (
    <th
      className={`sortable${center ? " center" : ""}`}
      onClick={() => handleSort(col)}
      title={`Sort by ${label}`}
    >
      <span className="sm-th-inner">
        {label}
        <span className="sm-sort-icon">{sortIcon(col)}</span>
      </span>
    </th>
  );

  return (
    <div className="sm-page">
      <div className="sm-header">
        <div className="sm-header-left">
          <h1>Sector Master</h1>
          <p>{filtered.length} sector{filtered.length !== 1 ? "s" : ""} found</p>
        </div>
        {hasPermission("CREATE_SECTOR_MASTER") && (
          <button
            type="button"
          className="sm-btn-add"
          onClick={() => {
            setSelectedSector(null);
            setModalMode("add");
          }}
        >
          + Add New Sector
        </button>
        )}
      </div>

      <div className="sm-filters" style={{ justifyContent: "flex-end" }}>
        <div className="sm-filters-right">
          <select
            className="sm-input"
            value={descriptionFilter}
            onChange={(e) => setDescriptionFilter(e.target.value)}
            style={{ maxWidth: 220 }}
          >
            <option value="all">All Descriptions</option>
            <option value="with_description">With Description</option>
            <option value="without_description">Without Description</option>
          </select>
          <div className="sm-search-wrap">
            <SearchIcon />
            <input
              type="text"
              placeholder="Search by code, name, description..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={handleSearchKey}
            />
          </div>
          <button type="button" className="sm-btn-search" onClick={handleSearch}>
            Search
          </button>
        </div>
      </div>

      <div className="sm-table-wrap">
        <table className="sm-table">
          <thead>
            <tr>
              <ThCol col="id" label="ID" />
              <ThCol col="sector_code" label="Sector Code" />
              <ThCol col="sector_name" label="Sector Name" />
              <ThCol col="brief_description" label="Brief Description" />
              <th className="center">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <SkeletonRows />
            ) : paginatedSectors.length === 0 ? (
              <tr className="sm-empty-row">
                <td colSpan={5}>
                  <div className="sm-empty-icon">No Data</div>
                  <h3>No sectors found</h3>
                  <p>Try adjusting your search query.</p>
                </td>
              </tr>
            ) : (
              paginatedSectors.map((sector) => (
                <tr key={sector.id}>
                  <td>
                    <span className="sm-cell-id">#{sector.id}</span>
                  </td>
                  <td>
                    <span
                      style={{
                        fontWeight: 700,
                        background: "var(--bg-gray)",
                        color: "var(--text-body)",
                        padding: "4px 10px",
                        borderRadius: "6px",
                        fontSize: "13px",
                      }}
                    >
                      {sector.sector_code}
                    </span>
                  </td>
                  <td>
                    <div className="sm-cell-name">{sector.sector_name}</div>
                  </td>
                  <td>
                    <span
                      style={{
                        color: "#4b5563",
                        fontSize: "13px",
                        fontStyle: !sector.brief_description ? "italic" : "normal",
                      }}
                    >
                      {sector.brief_description || "No description provided"}
                    </span>
                  </td>
                  <td className="center">
                    <div
                      className="sm-actions-cell"
                      style={{ display: "flex", gap: 8, justifyContent: "center" }}
                    >
                    {hasPermission("CREATE_SECTOR_MASTER") && (
                      <button
                        type="button"
                        className="sm-icon-btn"
                        title="Edit"
                        onClick={() => handleEditPrompt(sector)}
                      >
                        <EditIcon />
                      </button>
                    )}  
                    {hasPermission("DELETE_SECTOR_MASTER") && (
                      <button 
                        type="button"
                        className="sm-icon-btn delete"
                        title="Delete"
                        onClick={() => handleDeletePrompt(sector)}
                      >
                        <TrashIcon />
                      </button>
                    )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>

        {!loading && filtered.length > 0 && (
          <div className="sm-table-footer">
            <span>Showing {paginatedSectors.length} of {filtered.length} sectors</span>

            {totalPages > 1 && (
              <div className="sm-pagination">
                <button
                  type="button"
                  className="sm-page-btn"
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage((prev) => prev - 1)}
                >
                  {"<"}
                </button>

                {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => {
                  if (
                    page === 1 ||
                    page === totalPages ||
                    (page >= currentPage - 1 && page <= currentPage + 1)
                  ) {
                    return (
                      <button
                        type="button"
                        key={page}
                        className={`sm-page-btn ${currentPage === page ? "active" : ""}`}
                        onClick={() => setCurrentPage(page)}
                      >
                        {page}
                      </button>
                    );
                  }

                  if (page === currentPage - 2 || page === currentPage + 2) {
                    return (
                      <span key={page} style={{ color: "var(--text-muted)", padding: "0 4px" }}>
                        ...
                      </span>
                    );
                  }

                  return null;
                })}

                <button
                  type="button"
                  className="sm-page-btn"
                  disabled={currentPage === totalPages}
                  onClick={() => setCurrentPage((prev) => prev + 1)}
                >
                  {">"}
                </button>
              </div>
            )}

            <span style={{ color: "var(--text-muted)", fontSize: 11 }}>
              Last refreshed: {formatAdminDate(new Date())}
            </span>
          </div>
        )}
      </div>

      {modalMode === "delete" && selectedSector && (
        <DeleteModal
          item={selectedSector}
          onClose={() => {
            if (!deleting) {
              setModalMode(null);
              setSelectedSector(null);
            }
          }}
          onConfirm={handleDeleteConfirm}
          submitting={deleting}
        />
      )}

      {(modalMode === "add" || modalMode === "edit") && (
        <AddModal
          initialData={selectedSector}
          mode={modalMode}
          onClose={() => {
            if (!saving) {
              setModalMode(null);
              setSelectedSector(null);
            }
          }}
          onSave={modalMode === "edit" ? handleUpdate : handleSaveAdd}
          submitting={saving}
        />
      )}
    </div>
  );
};

export default SectorMaster;
